import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Row, Col, FormGroup, Label, Input, Card, CardBody, CardHeader } from 'reactstrap';
import { fetchBeats, fetchDivisions, fetchRanges, fetchRanges2, fetchRounds, fetchRounds2, getAllUSer } from '../../store/actions';
import { createSelector } from 'reselect';

const UserManagementTable = () => {
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [formData, setFormData] = useState({
    division: '',
    range: '',
    round: '',
    beat: ''
  });

  const dispatch = useDispatch();
  const { roles, divisions, ranges, rounds, beats, error } = useSelector(
    (state) => state.Common || {}
  );

  const commonSelector = createSelector(
    (state) => state.Common,
    (Common) => ({
      allUsers: Common.allUsersnvehicle ? Common.allUsersnvehicle : [],
    })
  );
  const { allUsers } = useSelector(commonSelector);
  const dependentDropdowns = [
    {
      label: "Division",
      key: "division",
      data: divisions,
      fetch: fetchRanges2,
    },
    {
      label: "Range",
      key: "range",
      data: ranges,
      fetch: fetchRounds2,
    },
    {
      label: "Round",
      key: "round",
      data: rounds,
      fetch: fetchBeats,
    },
    {
      label: "Beat",
      key: "beat",
      data: beats,
      fetch: null,
    },
  ];

  // Load initial dropdown data only once when component mounts
  useEffect(() => {
    dispatch(getAllUSer());
    dispatch(fetchDivisions());
    // Only try to load initial data if we have form values on first render
    const { division, range, round, beat } = formData;
    if (division) {
      dispatch(fetchRanges(division));
      
      if (range) {
        dispatch(fetchRounds(range));
        
        if (round) {
          dispatch(fetchBeats(round));
        }
      }
    }
  }, []); // Empty dependency array means this runs only once on mount

  // Handle input changes with optional cascading fetches
  const handleInputChange = (e, key, fetchAction = null) => {
    e.preventDefault();

    const value = e.target.value;
  
    setFormData((prev) => {
      const updatedData = { ...prev, [key]: value };
      if (value === "0") {
        const keyIndex = dependentDropdowns.findIndex((dropdown) => dropdown.key === key);
        if (keyIndex !== -1) {
          for (let i = keyIndex + 1; i < dependentDropdowns.length; i++) {
            updatedData[dependentDropdowns[i].key] = undefined; // Reset the dependent dropdowns
          }
        }
      } else if (fetchAction) {
        dispatch(fetchAction(Number(value)));
      }
  
      return updatedData;
    });
  };

  // Filter users based on form data
  useEffect(() => {
    if (!allUsers?.length) return;
    
    let result = [...allUsers];
    
    if (formData.division && formData.division !== "0") {
      result = result.filter(user => user.divisionId === formData.division);
    }
    if (formData.range && formData.range !== "0") {
      result = result.filter(user => user.rangeId == formData.range);
    }
    
    if (formData.round && formData.round !== "0") {
      result = result.filter(user => user.roundId == formData.round);
    }
    
    if (formData.beat && formData.beat !== "0") {
      result = result.filter(user => user.beatId == formData.beat);
    }
    
    setFilteredUsers(result);
  }, [allUsers, formData.division, formData.range, formData.round, formData.beat]); // More specific dependencies

  return (
    <div className='mt-3'>
      <Card className="mb-4">
        <CardHeader>
          <h4 className="mb-0">User Filters</h4>
        </CardHeader>
        <CardBody>
          <Row>
            {dependentDropdowns.map(
              ({ label, key, data, fetch }, index) => (
                <Col md={3} key={key+label}>
                  <FormGroup>
                    <Label for={key}>{label}</Label>
                    <Input
                      type="select"
                      id={key}
                      value={formData[key] || ""}
                      onChange={(e) => handleInputChange(e, key, fetch)}
                      disabled={
                        (index > 0 &&
                          !formData[
                            ["division", "range", "round"][index - 1]
                          ]) ||
                          (index > 0 && formData["division"] === "0")
                      }
                    >
                      <option value="0">All</option>
                      {Array.isArray(data?.data) &&
                        data.data.map((item) => (
                          <option
                            key={item[`${key}Id`]}
                            value={item[`${key}Id`]}
                          >
                            {item.divisionName || item.rangeName || item.roundName || item.beatName|| item.name }
                          </option>
                        ))}
                    </Input>
                  </FormGroup>
                </Col>
              )
            )}
          </Row>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h4 className="mb-0">Users List</h4>
        </CardHeader>
        <CardBody>
          <Table responsive striped hover>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Contact</th>
                <th>Division</th>
                <th>Range</th>
                <th>Round</th>
                <th>Beat</th>
                <th>Role</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <tr key={user.userId}>
                    <td>{`${user.firstName || ''} ${user.lastName || ''}`}</td>
                    <td>{user.email}</td>
                    <td>{user.contactNumber}</td>
                    <td>{user.divisionName}</td>
                    <td>{user.rangeName}</td>
                    <td>{user.roundName}</td>
                    <td>{user.beatName || user.name}</td> 
                    <td>{user.roleName}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center">
                    No users found matching the selected filters
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </CardBody>
      </Card>
    </div>
  );
};

export default UserManagementTable;